import { useCallback, useEffect, useState } from "react";
import { api, fmtDateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, KeyRound, Trash2, ShieldCheck, User as UserIcon, Sparkles } from "lucide-react";

const RoleBadge = ({ role }) => (
  <span
    className={
      role === "admin"
        ? "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary"
        : "inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-border bg-secondary text-foreground/80"
    }
  >
    {role === "admin" ? <ShieldCheck className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}
    <span className="capitalize">{role}</span>
  </span>
);

// ---------- Add-user dialog ----------
function AddUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      toast.success(`Added ${data.name}`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="team-add-user-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="team-add-user-dialog">
        <DialogHeader>
          <DialogTitle>Add a team member</DialogTitle>
          <DialogDescription>
            They'll be able to sign in with the initial password and change it from their profile.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-name">Name</Label>
            <Input id="add-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="add-user-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-email">Email</Label>
            <Input id="add-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="add-user-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-pw">Initial password</Label>
            <Input id="add-pw" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={8} data-testid="add-user-password" />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="add-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="add-user-submit">
              {busy ? "Creating…" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reset-password dialog ----------
function ResetPasswordDialog({ user, open, onOpenChange, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${user.name}`);
      setPw("");
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setPw(""); onOpenChange(v); }}>
      <DialogContent data-testid="reset-pw-dialog">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            Set a new password for <span className="font-medium">{user?.email}</span>. Share it privately — they can change it after logging in.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rp-pw">New password</Label>
            <Input id="rp-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} data-testid="reset-pw-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="reset-pw-submit">{busy ? "Saving…" : "Reset password"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Main page ----------
export default function Team() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`Removed ${deleteTarget.name}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setDeleteTarget(null);
    }
  };

  const resetTutorial = async (u) => {
    try {
      await api.post(`/users/${u.id}/reset-tutorial`);
      toast.success(`${u.name} will see the tutorial on next page load`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const resetTutorialAll = async () => {
    if (!window.confirm("Force EVERYONE (including you) to see the tutorial again on next page load?")) return;
    try {
      const { data } = await api.post("/users/reset-tutorial-all");
      toast.success(`Reset tutorial for ${data.users_reset} users`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  if (me && me.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-6 lg:p-10 max-w-[1200px] animate-fade-in">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Access</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Team</h1>
          <p className="text-muted-foreground mt-1">Manage who can sign in to the console and what they can do.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={resetTutorialAll}
            data-testid="team-reset-tutorial-all"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Reset tutorial for everyone
          </Button>
          <AddUserDialog onCreated={load} />
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm" data-testid="team-table">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Role</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Created</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground italic">No users yet</td></tr>
            )}
            {users.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-secondary/30">
                  <td className="px-4 py-3 font-medium">
                    {u.name} {isSelf && <span className="text-xs text-muted-foreground font-normal">(you)</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    {isSelf ? (
                      <RoleBadge role={u.role} />
                    ) : (
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="h-8 w-[130px]" data-testid={`role-select-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{fmtDateShort(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetTutorial(u)}
                        data-testid={`reset-tutorial-${u.email}`}
                        title="Force this user to see the tutorial again"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setResetTarget(u)}
                        data-testid={`reset-pw-${u.email}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Reset
                      </Button>
                      {!isSelf && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteTarget(u)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-user-${u.email}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ResetPasswordDialog
        user={resetTarget}
        open={!!resetTarget}
        onOpenChange={(v) => !v && setResetTarget(null)}
        onDone={load}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll lose access to the console immediately. This can't be undone (though you can re-invite them later).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-user-confirm-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
